'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { SearchX } from 'lucide-react';
import { Button, Card, ChipGroup, Heading, InputChip, Text } from '@zillow/constellation-10';

import { css } from '@/styled-system/css';
import { token } from '@/styled-system/tokens';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { SearchListingsRequest } from '@/src/lib/api';
import type { FilterPill } from '@/src/lib/filters';
import { type FilterState, useFilterLabels, useFilters } from '@/src/lib/filters';
import { useExcludedListingCounts } from './useExcludedListingCounts';

const panel = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'default',
  textAlign: 'center',
  paddingBlock: 'loose',
});

const message = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'tightest',
});

function pillId({ key, value }: FilterPill): string {
  return `${key}:${value ?? ''}`;
}

/**
 * The SRP's plain empty card. Shared with the past-the-last-page case in
 * `LiveListingsGrid`, which is empty without the search itself being empty, so
 * the wording lives in one place.
 */
export function NoListingsCard() {
  const t = useTranslations('search');
  return (
    <Card className={css({ textAlign: 'center' })}>
      <Card.Body>{t('noListingsFoundLong')}</Card.Body>
    </Card>
  );
}

type Props = {
  /** The live SRP search request the empty result came from. */
  request: SearchListingsRequest;
  /**
   * Server-resolved unfiltered count for `request`. Lets the panel's first paint
   * report the total and decide whether the filters are the reason, instead of
   * rearranging itself once the client probe lands.
   */
  initialUnfilteredTotal?: number | null;
  /** Server-computed slug-merged filter set — see the `pills` note below. */
  initialEffectiveFilters?: FilterState;
};

/**
 * SRP zero-results state. Ported from legacy hotpads-web's NoListingsFound +
 * FilterPills: tells the renter how many listings their filters are keeping
 * out, then offers each filter back as a removable chip labelled with the
 * listings that filter alone is excluding.
 *
 * Renders only on a search that returned nothing — every chip count is its own
 * probe request (see {@link useExcludedListingCounts}).
 */
export function NoResultsPanel({ request, initialUnfilteredTotal, initialEffectiveFilters }: Props) {
  const t = useTranslations('search');
  const tFilters = useTranslations('filterModal');
  const format = useFormatter();
  const { filters, activeFilterPills, removeFilter, resetFilters, hasInteracted } = useFilters();
  const { getActiveFilterPills } = useFilterLabels();

  // Slug-implied filters reach FilterContext through an effect, so on a slug SRP
  // (/rooms-for-rent) the context holds only the URL filters during SSR and first
  // paint — leaving this panel chipless, so the grid rendered the neutral card and
  // swapped it for the panel after hydration. Prefer the server's set until the
  // context catches up; both come from mergeSlugDefaultsWithExplicit, so they agree.
  const useServerFilters = !hasInteracted && activeFilterPills.length === 0 && initialEffectiveFilters !== undefined;
  const effectiveFilters = useServerFilters ? initialEffectiveFilters! : filters;
  const pills = useServerFilters ? getActiveFilterPills(effectiveFilters) : activeFilterPills;

  const { unfilteredTotal, chips } = useExcludedListingCounts({
    request,
    filters: effectiveFilters,
    pills,
    initialUnfilteredTotal,
  });

  // Nothing to offer back, or the server already proved this viewport is empty
  // unfiltered — either way removing filters cannot help. Reads the prop, not the
  // live count: deciding this after first paint would swap the whole panel for
  // the one-line card.
  if (chips.length === 0 || initialUnfilteredTotal === 0) return <NoListingsCard />;

  // Only on positive evidence: an unprobed, in-flight, or failed chip is `null`,
  // not 0, so it keeps this false.
  const noSingleFilterHelps = chips.every(({ count }) => count === 0);

  // Rank and prune only once every count is in. Reordering as each probe lands
  // moves chips under the renter's pointer, and pruning early would drop the
  // chips past MAX_PROBED_FILTERS (permanently `null`) out of the list entirely.
  const allCountsKnown = chips.every(({ count }) => count !== null);
  const offeredChips = allCountsKnown
    ? chips.filter(({ count }) => noSingleFilterHelps || count !== 0).sort((a, b) => b.count! - a.count!)
    : chips;

  return (
    <Card className={css({ textAlign: 'center' })}>
      <Card.Body>
        <div className={panel}>
          <LucideIcon icon={SearchX} size="xl" color={token.var('colors.text.subtle')} />

          {/* One live region over every line whose wording changes as the probes
              resolve, so a screen reader hears the corrected guidance. */}
          <div className={message} role="status" aria-live="polite">
            <Heading level={2} textStyle="heading-sm">
              {t('noResultsWithFilters')}
            </Heading>
            {unfilteredTotal ? (
              <Text textStyle="body" color="text.subtle">
                {t('excludedByFilters', { count: unfilteredTotal })}
              </Text>
            ) : null}
            <Text textStyle="body-sm" color="text.subtle">
              {noSingleFilterHelps ? t('removeSomeFiltersPrompt') : t('removeAFilterPrompt')}
            </Text>
          </div>

          {/* Stable name: the visible prompt flips wording, so it cannot double
              as the accessible label. */}
          <ChipGroup aria-label={t('removableFiltersAria')}>
            {offeredChips.map(({ pill, count }) => (
              <InputChip key={pillId(pill)} onClose={() => removeFilter(pill.key, pill.value)}>
                {count ? `${pill.label} (${format.number(count)})` : pill.label}
              </InputChip>
            ))}
          </ChipGroup>

          <Button tone="neutral" emphasis="bare" size="sm" onClick={resetFilters}>
            {tFilters('clearAll')}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
